import * as React from 'react';

import {
  ListScreenLayout,
  Pagination,
  type PaginationProps,
  SearchField,
} from '@rematter/ui-web';
import { useAtom, useAtomValue } from 'jotai';

import * as state from '../../_state/transactions.state';
import { useTranslations } from '../../i18n/useTranslations';

import { DirectionToggle } from './filters/DirectionToggle';
import { TransactionsInboundDataGrid } from './TransactionsInboundDataGrid';
import { TransactionsOutboundDataGrid } from './TransactionsOutboundDataGrid';

import { useDebouncedAtom } from '~/shared/hooks/useDebouncedAtom';

//
// ⚛️ ⚛️ ⚛️ TRANSACTIONS LIST VIEW ⚛️ ⚛️ ⚛️
//
export const TransactionsListView = () => {
  const t = useTranslations();

  // Atoms
  const direction = useAtomValue(state.$direction);
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const [searchTerm, setSearchTerm] = useDebouncedAtom(state.$searchTerm, 300);

  // Data hooks
  const inboundTotal = useAtomValue(state.$inboundTotal);
  const outboundTotal = useAtomValue(state.$outboundTotal);

  // Memos
  const isInbound = direction === 'inbound';
  const total = isInbound ? inboundTotal : outboundTotal;

  // Callbacks
  const handlePaginationChange = React.useCallback<
    NonNullable<PaginationProps['onChange']>
  >(
    ({ limit: newLimit, offset: newOffset }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );

  return (
    <div className="@TransactionsListView flex h-full flex-col">
      <ListScreenLayout
        filters={
          <>
            <DirectionToggle />

            <SearchField
              value={searchTerm ?? ''}
              onChange={setSearchTerm}
              placeholder={t.transactionsView.toolbar.search}
              className="w-64"
            />
          </>
        }
        footer={
          total > limit ? (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          ) : undefined
        }
      >
        {isInbound ? (
          <TransactionsInboundDataGrid />
        ) : (
          <TransactionsOutboundDataGrid />
        )}
      </ListScreenLayout>
    </div>
  );
};
